import { observer } from "mobx-react-lite";
import * as React from "react";
import { classNames } from "src/common/classNames";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import {
  AbstractFilters,
  AbstractRow,
  RtVerticalMode,
} from "src/components/ReactiveTable/RtTypes";
import { calculateFixedColumns } from "src/components/ReactiveTable/utils/calculateFixedColumns";
import { updateColumnWidth } from "src/components/ReactiveTable/utils/updateColumnWidth";

import { ColumnFiltersDict } from "../AntDropdownFilter";
import { mergeTableComponents } from "../mergeComponents";
import { TableEmpty, TableError, TableLoading } from "../states";
import { RowClickProps } from "./componentProps";
import { PartialTableComponents } from "./componentRegistry";
import * as styles from "./ReactiveTable.module.less";

/**
 * Props для главного компонента ReactiveTable
 */
export type PropsReactiveTable<
  TRow extends AbstractRow,
  TFilters extends AbstractFilters,
> = {
  // ========== Обязательные ==========
  /**
   * Store с данными и логикой таблицы
   */
  store: RtStore<TRow, TFilters>;

  // ========== Опциональные ==========
  /**
   * Определение колонок таблицы
   */
  columns?: RtColumn<TRow>[];

  /**
   * Минимальная высота таблицы
   */
  minHeight?: React.CSSProperties["minHeight"];

  /**
   * Функция для отображения общего количества записей
   */
  showTotal?(count: number): string;

  /**
   * Показывать выбор количества строк на странице
   */
  showSizeChanger?: boolean;

  /**
   * Обработчик для кликов по строкам (click, link, custom)
   */
  rowClick?: RowClickProps<TRow>;

  /**
   * Фильтры для колонок (dropdown filters)
   */
  columnFilters?: ColumnFiltersDict;

  /**
   * Включить возможность изменения ширины колонок
   */
  resizableColumns?: boolean;

  /**
   * Режим вертикального отображения:
   * - fit: растянуть на всю высоту контейнера
   * - limit: растянуть только если контент большой
   * - normal: без адаптации к высоте
   */
  verticalMode?: RtVerticalMode;

  // ========== Новые возможности ==========
  /**
   * Переопределение стандартных компонентов таблицы
   */
  components?: PartialTableComponents<TRow, TFilters>;

  // ========== Стилизация ==========
  /**
   * CSS класс для корневого элемента
   */
  className?: string;
};

/**
 * Главный компонент модульной реактивной таблицы
 */
export const ReactiveTable = observer(
  <TRow extends AbstractRow, TFilters extends AbstractFilters>(
    props: PropsReactiveTable<TRow, TFilters>,
  ) => {
    const {
      store,
      columns,
      minHeight,
      showTotal = (count) => `Всего: ${count}`,
      showSizeChanger = true,
      rowClick,
      columnFilters,
      resizableColumns,
      verticalMode = "fit",
      components: customComponents,
      className,
    } = props;

    const { rows, visibleColumns, loading, prevError, fixedColumns } = store;

    const [tableContententRef, setTableContententRef] =
      React.useState<HTMLDivElement | null>(null);

    // Callback ref для установки ref элемента
    const tableContentCallbackRef = (node: HTMLDivElement | null) =>
      setTableContententRef(node);

    // Устанавливаем колонки в store если они переданы
    React.useEffect(() => {
      store.init(columns).catch(store.onError);
    }, [store, columns]);

    // Устанавливаем verticalMode в store если передан
    React.useEffect(() => {
      if (verticalMode !== undefined) {
        store.setVerticalMode(verticalMode);
      }
    }, [store, verticalMode]);

    React.useEffect(() => {
      if (!tableContententRef || tableContententRef?.offsetWidth === 0) {
        return;
      }
      store.setColumnWidths(
        updateColumnWidth({
          columns: visibleColumns,
          configuredWidths: store.columnWidths,
          containerWidth: tableContententRef.offsetWidth,
          hasSelection: !!store.selectionType,
        }),
      );
    }, [tableContententRef, visibleColumns]);

    const colWidthsKey = Object.entries(store.columnWidths)
      .map((c) => `${c[0]}-${c[1]}`)
      .join(",");
    // Вычисляем позиции фиксированных колонок
    const fixedColumnsMap = React.useMemo(
      () =>
        calculateFixedColumns(
          fixedColumns,
          visibleColumns,
          store.columnWidths,
          !!store.selectionType,
        ),
      [visibleColumns, fixedColumns, colWidthsKey],
    );

    const buildContent = () => {
      if (prevError) return <TableError error={prevError} />;
      if (!loading && rows.length === 0) return <TableEmpty />;
      // Мержим компоненты: дефолтные + кастомные
      const components = mergeTableComponents<TRow, TFilters>(customComponents);
      const { Head, Row, Footer } = components;

      return (
        <>
          {/** заголовок и строки данных живут в общем контейнере, который обеспечивает скролл */}
          <div ref={tableContentCallbackRef} className={styles.tableContent}>
            <Head
              store={store}
              columnFilters={columnFilters}
              resizableColumns={resizableColumns}
              components={components}
              fixedColumnsMap={fixedColumnsMap}
            />
            {rows.map((row) => {
              const rowKey = store.getRowKey(row);
              const isSelected = store.isSelected(rowKey);
              return (
                <Row
                  key={rowKey}
                  row={row}
                  store={store}
                  columns={store.visibleColumns}
                  isSelected={isSelected}
                  components={components}
                  fixedColumnsMap={fixedColumnsMap}
                  clickProps={rowClick}
                />
              );
            })}
          </div>
          <Footer
            store={store}
            showTotal={showTotal}
            showSizeChanger={showSizeChanger}
          />
        </>
      );
    };

    return (
      <div
        className={classNames([
          styles.tableLayout,
          styles[store.verticalMode],
          className,
        ])}
        style={{ minHeight }}
        data-table-container
      >
        {loading && <TableLoading />}
        {buildContent()}
      </div>
    );
  },
);
